<template>
  <div class="file-uploader-component">
    <div v-if="existingFiles.length > 0" class="attachment-previews-grid mb-2">
      <div
        v-for="file in existingFiles"
        :key="file.id || file.fileUrl"
        class="attachment-preview-item"
      >
        <a
          v-if="file.fileType && file.fileType.startsWith('image/')"
          :href="file.fileUrl"
          target="_blank"
          class="attachment-thumbnail"
          :title="`Xem ảnh: ${file.originalFileName}`"
        >
          <img :src="file.fileUrl" :alt="file.originalFileName" />
        </a>

        <a
          v-else-if="file.fileType && file.fileType.startsWith('video/')"
          :href="file.fileUrl"
          target="_blank"
          class="attachment-thumbnail"
        >
          <video :src="file.fileUrl" class="img-fluid rounded mb-2"></video>
        </a>

        <a
          v-else
          :href="file.fileUrl"
          target="_blank"
          class="file-icon-preview"
          :title="file.originalFileName"
        >
          <i :class="getFileIconClass(file.fileType)"></i>
          <span class="file-name">{{ file.originalFileName }}</span>
        </a>

        <button
          v-if="!isViewMode"
          type="button"
          @click.stop="emitRemoveExisting(file)"
          class="attachment-remove-btn"
          title="Xóa file này"
        >
          &times;
        </button>
      </div>
    </div>

    <div v-if="modelValue.length > 0" class="attachment-previews-grid mb-2">
      <div
        v-for="(file, index) in modelValue"
        :key="index"
        class="attachment-preview-item"
      >
        <div v-if="file.type.startsWith('image/')" class="attachment-thumbnail">
          <img :src="getFilePreviewUrl(file)" :alt="file.name" />
        </div>
        <div v-else class="file-icon-preview">
          <i :class="getFileIconClass(file.type)"></i>
          <span class="file-name">{{ file.name }}</span>
        </div>

        <button
          type="button"
          @click.stop="removeNewFile(index)"
          class="attachment-remove-btn"
          title="Bỏ chọn file này"
        >
          &times;
        </button>
      </div>
    </div>

    <div v-if="!isViewMode">
      <div
        class="file-drop-zone"
        :class="{ 'drag-over': isDragOver }"
        @dragover.prevent="isDragOver = true"
        @dragleave.prevent="isDragOver = false"
        @drop.prevent="handleFileDrop"
        @paste.prevent="handlePaste"
        tabindex="0"
      >
        <p class="text-muted mb-2">
          Kéo thả file vào đây, <b>Dán (Paste)</b> hoặc
        </p>

        <button
          type="button"
          class="btn btn-light btn-sm"
          @click="triggerFileInput"
          :disabled="isUploading"
        >
          <i class="fa fa-plus"></i> Chọn file
        </button>

        <input
          type="file"
          ref="fileInputRef"
          @change="onFileChange"
          class="d-none"
          :multiple="multiple"
          :accept="accept"
        />
      </div>

      <div
        v-if="isUploading"
        class="spinner-border spinner-border-sm ml-2 mt-2"
        role="status"
      >
        <span class="sr-only">Đang tải lên...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";

// --- Định nghĩa Props ---
const props = defineProps({
  // v-model: Mảng các file MỚI do người dùng chọn (kiểu [File, File])
  modelValue: {
    type: Array,
    default: () => [],
  },
  // Mảng các file ĐÃ TỒN TẠI trên server
  existingFiles: {
    type: Array,
    default: () => [],
  },
  // Chế độ chỉ xem? (Ẩn nút upload/xóa)
  isViewMode: {
    type: Boolean,
    default: false,
  },
  // Đang upload? (Khóa nút, hiển thị spinner)
  isUploading: {
    type: Boolean,
    default: false,
  },
  // Cho phép chọn nhiều file?
  multiple: {
    type: Boolean,
    default: true,
  },
  // Lọc loại file (vd: "image/*", ".pdf")
  accept: {
    type: String,
    default: "*/*",
  },
});

// --- Định nghĩa Emits ---
const emit = defineEmits(["update:modelValue", "remove-existing"]);

// --- State nội bộ ---
const isDragOver = ref(false);
const fileInputRef = ref(null);

// --- Xử lý Logic File ---

/**
 * Hàm trung tâm xử lý danh sách file đầu vào
 */
function addFiles(fileList) {
  if (!fileList || fileList.length === 0) return;

  // Lấy danh sách file mới (copy từ v-model)
  let newFiles = [...props.modelValue];

  if (props.multiple) {
    // Nếu cho phép nhiều file, thêm tất cả vào mảng
    newFiles.push(...Array.from(fileList));
  } else {
    // Nếu chỉ 1 file, ghi đè
    newFiles = [fileList[0]];
  }

  // Phát sự kiện 'update:modelValue' để cập nhật v-model ở component cha
  emit("update:modelValue", newFiles);
}

/**
 * Xử lý khi chọn file từ nút bấm
 */
function onFileChange(event) {
  addFiles(event.target.files);
}

/**
 * Xử lý khi kéo thả
 */
function handleFileDrop(event) {
  isDragOver.value = false;
  addFiles(event.dataTransfer.files);
}

/**
 * Xử lý khi Dán (Paste)
 */
function handlePaste(event) {
  addFiles(event.clipboardData.files);
}

/**
 * Kích hoạt input file ẩn
 */
function triggerFileInput() {
  if (fileInputRef.value) {
    fileInputRef.value.value = null; // Reset để đảm bảo sự kiện change
    fileInputRef.value.click();
  }
}

// --- Xử lý Xóa File ---

/**
 * Xóa file MỚI CHỌN (xử lý nội bộ)
 */
function removeNewFile(index) {
  let newFiles = [...props.modelValue];
  newFiles.splice(index, 1);
  emit("update:modelValue", newFiles);
}

/**
 * Xóa file ĐÃ TỒN TẠI (báo cho component cha)
 */
function emitRemoveExisting(file) {
  // Component cha sẽ lắng nghe sự kiện này và gọi API xóa
  emit("remove-existing", file);
}

// --- Helpers ---

/**
 * Tạo URL tạm thời để xem trước ảnh MỚI CHỌN
 */
function getFilePreviewUrl(file) {
  return URL.createObjectURL(file);
}

/**
 * Trả về class icon dựa trên MIME type
 */
function getFileIconClass(fileType) {
  if (!fileType) return "fa fa-file-o fa-2x text-muted";

  if (fileType.startsWith("image/"))
    return "fa fa-file-image-o fa-2x text-info";
  if (fileType.startsWith("video/"))
    return "fa fa-file-video-o fa-2x text-warning";
  if (fileType.startsWith("audio/"))
    return "fa fa-file-audio-o fa-2x text-danger";
  if (fileType.includes("pdf")) return "fa fa-file-pdf-o fa-2x text-danger";
  if (fileType.includes("word")) return "fa fa-file-word-o fa-2x text-primary";
  if (fileType.includes("excel") || fileType.includes("spreadsheet"))
    return "fa fa-file-excel-o fa-2x text-success";
  if (fileType.includes("zip") || fileType.includes("archive"))
    return "fa fa-file-archive-o fa-2x text-muted";

  return "fa fa-file-o fa-2x text-muted";
}
</script>

<style scoped>
/* Style cho grid preview (Lấy từ code của bạn) */
.attachment-previews-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}

.attachment-preview-item {
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #dee2e6;
}

.attachment-thumbnail {
  cursor: pointer;
}

.attachment-thumbnail img,
.attachment-thumbnail video {
  width: 100%;
  height: 90px;
  object-fit: cover;
  display: block;
}

.file-icon-preview {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 90px;
  background-color: #f8f9fa;
  padding: 10px;
  text-align: center;
  text-decoration: none;
  color: #495057;
}

.file-icon-preview .file-name {
  font-size: 11px;
  margin-top: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.attachment-remove-btn {
  position: absolute;
  top: -5px;
  right: -5px;
  background: white;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  line-height: 18px;
  padding: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  color: red;
  border: none;
  cursor: pointer;
}

/* Style cho khu vực kéo thả (Lấy từ code trước) */
.file-drop-zone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background-color: #f8f9fa;
  transition: background-color 0.2s ease, border-color 0.2s ease;
  cursor: pointer;
}
.file-drop-zone p {
  margin-bottom: 10px;
  color: #6c757d;
  font-size: 0.9rem;
}
.file-drop-zone:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}
.file-drop-zone.drag-over {
  background-color: #e9ecef;
  border-color: #007bff;
}
.file-drop-zone .btn-light {
  border: 1px solid #ccc;
}
</style>
